// API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Analyzes survey data based on user input
 * @param {string} userInput - The user's query
 * @param {string|null} threadId - Optional thread ID for continuing a conversation
 * @param {string} accessToken - Access token for authorization
 * @returns {Promise<Object>} The analysis result
 */
export const analyzeSurvey = async (userInput, threadId = null, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/analyze/graph`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      thread_id: threadId,
      user_input: userInput
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = 
      (errorData && errorData.detail) 
        ? errorData.detail 
        : `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Analyzes survey data with streaming SSE support
 * @param {string} userInput - The user's query
 * @param {string|null} threadId - Optional thread ID for continuing a conversation
 * @param {string} accessToken - Access token for authorization
 * @param {Function} onMessage - Callback function to handle streaming messages
 * @param {Function} onError - Callback function to handle errors
 * @param {Function} onComplete - Callback function when stream completes
 * @returns {Function} Cleanup function to close the EventSource
 */
export const analyzeSurveyStream = (userInput, threadId = null, accessToken, onMessage, onError, onComplete) => {
  // For SSE with POST, we need to use fetch with ReadableStream
  const controller = new AbortController();
  
  const startStream = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/analyze/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          thread_id: threadId,
          user_input: userInput
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = 
          (errorData && errorData.detail) 
            ? errorData.detail 
            : `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = ''; // Buffer to accumulate incomplete messages

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('Stream completed normally');
            onComplete();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk; // Add new chunk to buffer
          
          // Split by lines and process complete lines
          const lines = buffer.split('\n');
          
          // Keep the last line in buffer (might be incomplete)
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.slice(6); // Remove 'data: ' prefix
                
                // Skip empty data lines
                if (jsonData.trim() === '') {
                  continue;
                }
                
                const data = JSON.parse(jsonData);
                onMessage(data);
              } catch (error) {
                console.error('Error parsing SSE message:', error);
                console.error('Problematic line:', line);
                // Continue processing other messages instead of failing completely
              }
            } else if (line.startsWith('event: complete')) {
              console.log('Received completion event');
              onComplete();
              return;
            }
          }
        }
      } catch (readerError) {
        // Handle reader-specific errors (like incomplete chunked encoding)
        if (readerError.name === 'AbortError') {
          console.log('Stream was aborted by user');
          return;
        }
        
        console.error('Reader error:', readerError);
        
        // Check if we received any data before the error
        if (buffer.trim()) {
          console.log('Stream ended with incomplete data, treating as completion');
          onComplete();
        } else {
          throw readerError;
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Stream was aborted by user');
        return;
      }
      
      console.error('Stream error:', error);
      
      // Provide more specific error messages
      let errorMessage = error.message;
      if (error.message.includes('network error') || error.message.includes('ERR_INCOMPLETE_CHUNKED_ENCODING')) {
        errorMessage = 'Network connection was interrupted. The analysis may have completed successfully.';
      }
      
      onError(new Error(errorMessage));
    }
  };

  startStream();

  // Return cleanup function
  return () => {
    controller.abort();
  };
};